import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { useRBAC } from '../hooks/useRBAC';
import toast from 'react-hot-toast';

const money = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');

export default function DemoPackCard() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useRBAC();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get('/api/demo/status'); setSt(r.data); } catch {} };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/demo/seed');
      setSt(r.data);
      toast.success(r.data.created ? 'Demo market loaded — 12 hot leads, verified contacts and a signed proposal are ready' : 'Demo market is already loaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the demo pack'); }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true);
    try { const r = await api.post('/api/demo/reset'); setSt(r.data); toast.success('Demo market rebuilt'); } catch { toast.error('Reset failed'); }
    setBusy(false);
  };

  const p = st?.proposal;
  return (
    <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-6" data-testid="demo-pack-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-400/20 text-amber-700 text-[10px] font-bold uppercase tracking-widest mb-2"><i className="fa-solid fa-box-open" /> Demo Data Pack</span>
          <h3 className="font-heading text-lg font-bold text-text-primary">Never test on an empty screen</h3>
          <p className="text-xs text-text-muted mt-1 max-w-xl">One click seeds a sample market — <strong>{st?.market || 'Montclair–Glen Ridge, NJ'}</strong> — with hot leads, verified callable contacts and a finished, signed proposal. Every record is clearly labelled as demo and never touches your live pipeline.</p>
        </div>
        {!st?.seeded ? (
          <button onClick={seed} disabled={busy} className="px-6 py-3 rounded-xl text-sm font-bold text-[#0C2417] shadow-md hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-60" style={{ background: '#FAAB1C' }} data-testid="demo-pack-load-btn">
            {busy ? <><i className="fa-solid fa-circle-notch fa-spin mr-2" />Loading…</> : <><i className="fa-solid fa-wand-magic-sparkles mr-2" />Load demo market</>}
          </button>
        ) : (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="demo-pack-ready"><i className="fa-solid fa-circle-check" /> Loaded</span>
        )}
      </div>

      {st?.seeded && (
        <div className="mt-5 grid sm:grid-cols-4 gap-3" data-testid="demo-pack-summary">
          {[
            { label: 'Sample leads', value: st.leads, icon: 'fa-house-signal', to: '/leads?source=demo_pack' },
            { label: 'Verified contacts', value: st.verified_contacts, icon: 'fa-phone-volume', to: st.top_lead_id ? `/leads/${st.top_lead_id}` : '/leads' },
            { label: 'Hot / High leads', value: st.hot_leads, icon: 'fa-fire', to: '/map' },
            { label: 'Signed proposal', value: p ? money(p.net_cost) : '—', icon: 'fa-file-signature', to: p ? `/proposals/${p.id}/view` : '/proposals' },
          ].map(k => (
            <button key={k.label} onClick={() => navigate(k.to)} className="text-left bg-surface rounded-xl border border-border p-4 hover:border-primary/50 transition-colors" data-testid={`demo-pack-stat-${k.label.split(' ')[0].toLowerCase()}`}>
              <i className={`fa-solid ${k.icon} text-primary text-sm mb-2 block`} />
              <p className="font-heading text-xl font-bold text-text-primary tabular-nums">{k.value}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">{k.label} →</p>
            </button>
          ))}
        </div>
      )}

      {st?.seeded && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          {p && <a href={`/p/${p.share_token}`} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline" data-testid="demo-pack-web-proposal"><i className="fa-solid fa-arrow-up-right-from-square mr-1" />Homeowner web proposal</a>}
          {p && <button onClick={() => navigate(`/proposals/${p.id}/view`)} className="font-semibold text-primary hover:underline" data-testid="demo-pack-open-proposal"><i className="fa-solid fa-file-pdf mr-1" />Open proposal + 13-page PDF</button>}
          {p?.rooftop_status === 'processing' && <span className="text-text-muted"><i className="fa-solid fa-satellite fa-spin mr-1" />Satellite roof render in progress…</span>}
          {isSuperAdmin && <button onClick={reset} disabled={busy} className="ml-auto font-semibold text-text-muted hover:text-destructive" data-testid="demo-pack-reset-btn"><i className="fa-solid fa-rotate mr-1" />Rebuild demo market</button>}
        </div>
      )}
    </div>
  );
}
